import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import instantsearch from 'instantsearch.js';
import { searchBox, hits, pagination } from 'instantsearch.js/es/widgets';
import { connectRefinementList } from 'instantsearch.js/es/connectors';
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import styles from './Browse.module.scss';
import config from '../../config';
import { Search } from 'lucide-react';
import { renderUserBrowseCardMarkup } from 'components/Browse/UserBrowseCard/UserBrowseCard';
import FilterBar from '../../components/Browse/FilterBar';
import FilterModal from '../../components/Browse/FilterModal/FilterModal';
import Loader from 'components/Loader/Loader';
import {
  syncBrowseInviteIslands,
  teardownBrowseInviteIslands,
} from 'components/Browse/InviteToProjectButton/mountBrowseInviteIslands';
import { useViewerCollaborations } from 'components/Browse/InviteToProjectButton/useViewerCollaborations';
import InviteProjectChooser from 'components/Browse/InviteToProjectButton/InviteProjectChooser';
import { isFreelancerInvitedToBrief } from 'components/Browse/InviteToProjectButton/briefInviteEligibility';
import { trackEvent } from 'helpers/Analytics';
import { addToTalentPool, removeFromTalentPool } from 'redux/modules/portfolio';
import { inviteUser } from 'redux/modules/briefDetails';
import { setSuccessMessage } from 'redux/modules/successMessage';
import { INVITE_SENT_SUCCESS } from 'constants/successMessages';

// Align with Bootstrap 3 `$screen-xs-max` (used in Browse.module.scss).
const MOBILE_INVITE_MAX_WIDTH_PX = 767;

// InstantSearch pagination: visible numbered links = min(2 * padding + 1, totalPages).
const PAGINATION_PADDING_DESKTOP = 3;
const PAGINATION_PADDING_MOBILE = 1;

/**
 * Parses URL query string into the filter params supported by this page.
 * Supports the same params as the legacy /browse page:
 *   role, skill, location — mapped to refinementList facets
 *   q / query            — mapped to the search box query
 */
function parseUrlFilters(search) {
  const params = queryString.parse(search);
  const toArray = (val) => {
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  };
  return {
    roles: toArray(params.role),
    skills: toArray(params.skill),
    locations: toArray(params.location),
    query: params.q || params.query || '',
  };
}

/**
 * Fetches facet values from Typesense for the given URL filter params and returns
 * a refinementList map with correctly-cased values ready for use in initialUiState.
 *
 * One search per attribute (not per value) — all raw values for that attribute
 * are matched case-insensitively against the returned facet keys, keeping the
 * total number of Typesense requests as small as possible.
 *
 * If the lookup fails, raw URL values are used as-is so filters still apply
 * (casing may differ but is better than no filters at all).
 */
async function resolveUrlFilterRefinements(searchClient, urlFilters) {
  // Build one request per attribute that has at least one value.
  const attributeRequests = [
    { attribute: 'roles', rawValues: urlFilters.roles },
    { attribute: 'skills', rawValues: urlFilters.skills },
    { attribute: 'location.text', rawValues: urlFilters.locations },
  ].filter(({ rawValues }) => rawValues.length > 0);

  if (!attributeRequests.length) return {};

  let facetResults;
  try {
    const response = await searchClient.search(
      attributeRequests.map(({ attribute }) => ({
        indexName: 'users_search',
        params: {
          query: '',
          hitsPerPage: 0,
          facets: [attribute],
          maxValuesPerFacet: 500,
        },
      }))
    );
    facetResults = response.results;
  } catch (e) {
    // Lookup failed — fall back to raw values so filters still apply.
    const fallback = {};
    attributeRequests.forEach(({ attribute, rawValues }) => {
      fallback[attribute] = rawValues;
    });
    return fallback;
  }

  const refinementList = {};

  attributeRequests.forEach(({ attribute, rawValues }, i) => {
    const facets = facetResults[i]?.facets?.[attribute];
    const canonicalKeys = facets ? Object.keys(facets) : [];
    rawValues.forEach((rawValue) => {
      const needle = rawValue.toLowerCase();
      const match = canonicalKeys.find((k) => k.toLowerCase() === needle);
      if (!refinementList[attribute]) refinementList[attribute] = [];
      // Use canonical casing if found, otherwise keep the raw value.
      refinementList[attribute].push(match || rawValue);
    });
  });

  return refinementList;
}

/**
 * Creates a virtual (no-DOM) refinement list widget for an attribute.
 * Registering these before search.start() means InstantSearch always has
 * the facets active — so initialUiState is applied reliably and there is
 * no conflict when the modal's FacetSelector widgets mount later.
 */
function createVirtualRefinementList(attribute, options = {}) {
  return connectRefinementList(() => {})({
    attribute,
    limit: 1,
    operator: 'and',
    ...options,
  });
}

const Browse = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const authUser = useSelector((state) => state.auth && state.auth.user);
  const ownBriefs = useSelector(
    (state) => (state.auth && state.auth.ownBriefs) || {}
  );
  const briefEntities = useSelector(
    (state) => (state.entities && state.entities.briefs) || {}
  );
  const inviteEntities = useSelector(
    (state) => (state.entities && state.entities.invites) || {}
  );
  const inviteEntitiesRef = useRef(inviteEntities);
  inviteEntitiesRef.current = inviteEntities;

  const portfolio = useSelector((state) => state.portfolio);
  const viewerUserIdRef = useRef(null);
  const portfolioRef = useRef(portfolio);
  portfolioRef.current = portfolio;
  viewerUserIdRef.current =
    authUser && authUser.id != null ? authUser.id : null;

  const {
    collaborations,
    isLoadingOwnBriefs,
    collaborationsHydrationSignature,
  } = useViewerCollaborations(authUser, portfolio, ownBriefs, briefEntities);

  const viewerUsernameRef = useRef(null);
  viewerUsernameRef.current =
    authUser && authUser.username != null ? authUser.username : null;

  const searchContainerRef = useRef(null);
  const hitsContainerRef = useRef(null);
  const historyPushTimerRef = useRef(null);
  const paginationContainerRef = useRef(null);
  const pillsContainerRef = useRef(null);
  // Tracks the last search string we pushed so we can distinguish our own
  // history.push calls from external navigation (back/forward).
  const lastPushedSearchRef = useRef(location.search);
  // True while we are restoring state from a back/forward navigation.
  // Prevents onRender from pushing a new history entry mid-restore.
  const isRestoringRef = useRef(false);
  const paginationWidgetRef = useRef(null);
  const skipInitialPaginationResizeRef = useRef(true);

  const paginationCssClasses = useMemo(
    () => ({
      root: styles.paginationRoot,
      list: styles.paginationList,
      item: styles.paginationItem,
      link: styles.paginationLink,
      selectedItem: styles.paginationItemSelected,
      disabledItem: styles.paginationItemDisabled,
    }),
    []
  );

  const [searchInstance, setSearchInstance] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [inviteChooserModal, setInviteChooserModal] = useState(null);
  const sessionInviteKeysRef = useRef(new Set());
  const [sessionInviteEpoch, setSessionInviteEpoch] = useState(0);

  const [isMobileInvite, setIsMobileInvite] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(`(max-width: ${MOBILE_INVITE_MAX_WIDTH_PX}px)`)
      .matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const query = `(max-width: ${MOBILE_INVITE_MAX_WIDTH_PX}px)`;
    const mq = window.matchMedia(query);
    const onChange = () => setIsMobileInvite(mq.matches);
    onChange();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const isMobileInviteRef = useRef(isMobileInvite);
  isMobileInviteRef.current = isMobileInvite;

  const inviteIslandPropsRef = useRef({});

  useEffect(() => {
    let disposed = false;
    const urlFilters = parseUrlFilters(location.search);

    const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: config.typesense.apiKeySearchOnly,
        nodes: [{ url: config.typesense.endpoint }],
      },
      additionalSearchParameters: {
        query_by: 'displayname,bio,location.text,roles,skills',
        sort_by: 'pro:desc,rating:desc,last_active:desc',
      },
    });

    // Resolve URL params to correctly-cased facet values before starting search,
    // so we can pass them directly into initialUiState.
    resolveUrlFilterRefinements(
      typesenseInstantsearchAdapter.searchClient,
      urlFilters
    ).then((refinementList) => {
      if (disposed) return;

      const search = instantsearch({
        indexName: 'users_search',
        searchClient: typesenseInstantsearchAdapter.searchClient,
        initialUiState: {
          users_search: {
            ...(urlFilters.query ? { query: urlFilters.query } : {}),
            ...(Object.keys(refinementList).length ? { refinementList } : {}),
          },
        },
      });

      search.addWidgets([
        // Virtual widgets registered before start() so InstantSearch always has
        // these facets active. initialUiState applies to them immediately, and
        // the modal's FacetSelector/LocationSelector widgets co-exist without
        // conflict when they mount later.
        // All use operator:'or' (disjunctive) so refinements are stored
        // consistently in disjunctiveFacetsRefinements and initialUiState
        // applies correctly.
        createVirtualRefinementList('roles', { operator: 'and' }),
        createVirtualRefinementList('skills', { operator: 'and' }),
        createVirtualRefinementList('location.text', {
          operator: 'or',
          limit: 500,
        }),
        createVirtualRefinementList('location.country', { operator: 'or' }),

        searchBox({
          container: searchContainerRef.current,
          placeholder: 'Search across 1M+ Experts',
          showReset: true,
          showSubmit: false,
          showLoadingIndicator: false,
          cssClasses: {
            root: styles.searchBoxRoot,
            form: styles.searchBoxForm,
            input: styles.searchBoxInput,
          },
        }),

        hits({
          container: hitsContainerRef.current,
          cssClasses: {
            root: styles.hitsRoot,
            list: styles.hitsList,
            item: styles.hitsItem,
            emptyRoot: styles.noResults,
          },
          templates: {
            item: (hit) =>
              renderUserBrowseCardMarkup(hit, {
                viewerUserId: viewerUserIdRef.current,
                portfolio: portfolioRef.current,
                viewerUsername: viewerUsernameRef.current,
              }),
            empty: `<div class="${styles.noResults}">No results found. Try a different search.</div>`,
          },
        }),

        (() => {
          const paginationPadding = isMobileInviteRef.current
            ? PAGINATION_PADDING_MOBILE
            : PAGINATION_PADDING_DESKTOP;
          const widget = pagination({
            container: paginationContainerRef.current,
            padding: paginationPadding,
            cssClasses: paginationCssClasses,
          });
          paginationWidgetRef.current = widget;
          return widget;
        })(),
      ]);

      search.on('render', () => {
        queueMicrotask(() => {
          const el = hitsContainerRef.current;
          if (el) {
            syncBrowseInviteIslands(el, {
              history,
              getInviteProps: () => inviteIslandPropsRef.current,
            });
          }
        });
      });

      // On every render, sync the filter badge count and update the URL so
      // the current search state can be bookmarked or shared.
      let prevQuery = urlFilters.query;

      search.on('render', () => {
        const s = search.helper?.state;
        if (!s) return;

        // roles/skills use operator:'and' → facetsRefinements (conjunctive)
        // location.text/country use operator:'or' → disjunctiveFacetsRefinements
        const fr = s.facetsRefinements || {};
        const dr = s.disjunctiveFacetsRefinements || {};

        const roles = fr['roles'] || [];
        const skills = fr['skills'] || [];
        const locations = dr['location.text'] || [];
        const q = s.query || '';

        // Badge count: sum all active refinements
        const count =
          Object.values(dr).reduce((n, v) => n + v.length, 0) +
          Object.values(fr).reduce((n, v) => n + v.length, 0) +
          Object.values(s.toggleRefinements || {}).filter(Boolean).length;
        setActiveFilterCount(count);

        // Sync URL — arrays become repeated keys: ?skill=Figma&skill=Ada
        const params = {};
        if (roles.length) params.role = roles.length === 1 ? roles[0] : roles;
        if (skills.length)
          params.skill = skills.length === 1 ? skills[0] : skills;
        if (locations.length)
          params.location = locations.length === 1 ? locations[0] : locations;
        if (q) params.q = q;

        const newSearch = queryString.stringify(params, { encode: true });
        const currentSearch = window.location.search.replace(/^\?/, '');

        // Once the search state matches the current URL, the restore has settled —
        // clear the flag so subsequent user-driven changes can push to history.
        if (newSearch === currentSearch) {
          isRestoringRef.current = false;
          return;
        }

        // Don't push during a back/forward restore — wait for the restored
        // state to settle first. Also bail if the URL has changed externally
        // (i.e. currentSearch no longer matches what we last pushed).
        if (isRestoringRef.current) return;
        const lastPushed = lastPushedSearchRef.current.replace(/^\?/, '');
        if (currentSearch !== lastPushed) return;

        const queryChanged = q !== prevQuery;

        const doPush = () => {
          prevQuery = q;
          const target = newSearch ? `?${newSearch}` : '';
          lastPushedSearchRef.current = target;
          // Store the resolved refinements in history state so back/forward
          // can restore instantly without an async Typesense lookup.
          history.push({
            search: target,
            state: {
              refinementList: { roles, skills, 'location.text': locations },
              query: q,
            },
          });
        };

        if (queryChanged) {
          clearTimeout(historyPushTimerRef.current);
          historyPushTimerRef.current = setTimeout(() => {
            if (q) trackEvent('client-uses-browse-search-box', { query: q });
            doPush();
          }, 500);
        } else {
          clearTimeout(historyPushTimerRef.current);
          doPush();
        }
      });

      search.start();
      setSearchInstance(search);
    });

    return () => {
      teardownBrowseInviteIslands();
      paginationWidgetRef.current = null;
      skipInitialPaginationResizeRef.current = true;
      disposed = true;
      setSearchInstance((prev) => {
        if (prev) prev.dispose();
        return null;
      });
    };
  }, [paginationCssClasses]);

  useEffect(() => {
    if (
      !searchInstance ||
      typeof searchInstance.removeWidgets !== 'function' ||
      typeof searchInstance.addWidgets !== 'function'
    ) {
      return;
    }
    if (!paginationWidgetRef.current || !paginationContainerRef.current) {
      return;
    }
    if (skipInitialPaginationResizeRef.current) {
      skipInitialPaginationResizeRef.current = false;
      return;
    }

    const padding = isMobileInvite
      ? PAGINATION_PADDING_MOBILE
      : PAGINATION_PADDING_DESKTOP;

    searchInstance.removeWidgets([paginationWidgetRef.current]);
    const nextWidget = pagination({
      container: paginationContainerRef.current,
      padding,
      cssClasses: paginationCssClasses,
    });
    paginationWidgetRef.current = nextWidget;
    searchInstance.addWidgets([nextWidget]);
    if (typeof searchInstance.scheduleRender === 'function') {
      searchInstance.scheduleRender();
    }
  }, [searchInstance, isMobileInvite, paginationCssClasses]);

  useEffect(() => {
    if (!searchInstance || typeof searchInstance.scheduleRender !== 'function')
      return;
    searchInstance.scheduleRender();
  }, [
    searchInstance,
    authUser?.id,
    authUser?.username,
    portfolio,
    collaborationsHydrationSignature,
    isLoadingOwnBriefs,
    inviteEntities,
    sessionInviteEpoch,
  ]);

  const isBriefInviteDisabled = useCallback(
    (brief) => {
      const targetId = inviteChooserModal?.userId ?? null;
      if (!targetId || !brief || brief.id == null) {
        return false;
      }
      const key = `${String(brief.id)}:${String(targetId)}`;
      if (sessionInviteKeysRef.current.has(key)) {
        return true;
      }
      return isFreelancerInvitedToBrief(brief, targetId, inviteEntities);
    },
    [inviteChooserModal, inviteEntities, sessionInviteEpoch]
  );

  const sendInviteForBrief = useCallback(
    (brief, toUserId) => {
      if (
        !authUser ||
        !brief ||
        brief.id == null ||
        brief.id === '' ||
        toUserId == null ||
        toUserId === ''
      ) {
        return;
      }
      const dedupeKey = `${String(brief.id)}:${String(toUserId)}`;
      if (
        isFreelancerInvitedToBrief(brief, toUserId, inviteEntities) ||
        sessionInviteKeysRef.current.has(dedupeKey)
      ) {
        return;
      }
      trackEvent('user-invites-freelancer', {
        type: 'browse_test',
        loggedIn: !!authUser,
        userType: authUser.type || 'unknown',
      });
      dispatch(inviteUser(authUser, toUserId, brief))
        .then(() => {
          sessionInviteKeysRef.current.add(dedupeKey);
          setSessionInviteEpoch((n) => n + 1);
          dispatch(setSuccessMessage(INVITE_SENT_SUCCESS));
          setInviteChooserModal(null);
        })
        .catch(() => {});
    },
    [authUser, dispatch, inviteEntities]
  );

  inviteIslandPropsRef.current = {
    collaborations,
    isLoadingOwnBriefs: !!(authUser && authUser.username) && isLoadingOwnBriefs,
    isBriefInviteBlockedForUser: (brief, freelancerUserId) => {
      if (!brief || brief.id == null || freelancerUserId == null) {
        return false;
      }
      const key = `${String(brief.id)}:${String(freelancerUserId)}`;
      if (sessionInviteKeysRef.current.has(key)) {
        return true;
      }
      return isFreelancerInvitedToBrief(
        brief,
        freelancerUserId,
        inviteEntities
      );
    },
    isMobile: isMobileInvite,
    authUser,
    onSendInvite: sendInviteForBrief,
    onOpenChooserModal: setInviteChooserModal,
  };

  // When location.search changes due to back/forward navigation, restore the
  // search state to match the URL. We detect back/forward by comparing against
  // lastPushedSearchRef — if the new location differs from what we last pushed,
  // it must be an external navigation.
  useEffect(() => {
    if (!searchInstance) return;

    // Skip if this location change was caused by our own history.push
    if (location.search === lastPushedSearchRef.current) return;

    // Update the ref so onRender doesn't push over this restored state
    lastPushedSearchRef.current = location.search;

    // Cancel any pending debounced push (e.g. from typing before pressing back)
    clearTimeout(historyPushTimerRef.current);

    // Block onRender from creating new history entries until the restore settles
    isRestoringRef.current = true;

    const applyRefinements = (refinementList, query) => {
      const helper = searchInstance.helper;
      helper.clearRefinements();

      const conjunctive = ['roles', 'skills'];
      Object.entries(refinementList).forEach(([attribute, values]) => {
        if (conjunctive.includes(attribute)) {
          values.forEach((v) => helper.addFacetRefinement(attribute, v));
        } else {
          values.forEach((v) =>
            helper.addDisjunctiveFacetRefinement(attribute, v)
          );
        }
      });

      helper.setQuery(query || '');
      helper.search();
    };

    // If history state has the resolved refinements, use them instantly.
    // Otherwise fall back to async Typesense lookup (e.g. direct URL navigation).
    const historyState = location.state;
    if (historyState?.refinementList) {
      applyRefinements(historyState.refinementList, historyState.query || '');
    } else {
      const urlFilters = parseUrlFilters(location.search);
      resolveUrlFilterRefinements(searchInstance.client, urlFilters).then(
        (refinementList) => {
          applyRefinements(refinementList, urlFilters.query);
        }
      );
    }
  }, [location.search, searchInstance]);

  useEffect(() => {
    const root = hitsContainerRef.current;
    if (!root) return undefined;

    const onClick = (event) => {
      const btn = event.target.closest('[data-browse-shortlist]');
      if (!btn || !root.contains(btn)) return;
      event.preventDefault();
      event.stopPropagation();

      const username = btn.getAttribute('data-username');
      if (!username) return;

      if (!authUser) {
        window.location.href = '/signin';
        return;
      }

      const isInPool = btn.getAttribute('aria-pressed') === 'true';

      if (isInPool) {
        dispatch(removeFromTalentPool(username));
      } else {
        trackEvent('user-shortlists-freelancer', { shortlist_type: 'browse' });
        dispatch(addToTalentPool(username));
      }
    };

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [authUser, dispatch, history]);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Helmet
          title="Browse Freelancers | Twine"
          link={[
            {
              rel: 'canonical',
              href: config.baseUrl + '/browse',
            },
          ]}
          meta={[{ name: 'robots', content: 'noindex' }]}
        />

        <h1 className={styles.heading}>Browse</h1>

        {/* Loader shown until search instance is ready */}
        {!searchInstance && <Loader loading />}

        <div style={{ display: searchInstance ? 'block' : 'none' }}>
          <div className={styles.searchAndFiltersRow}>
            <div className={styles.searchWrapper}>
              <span className={styles.searchIcon}>
                <Search size={18} />
              </span>
              <div
                ref={searchContainerRef}
                className={styles.searchBoxContainer}
              />
            </div>

            <FilterBar
              search={searchInstance}
              activeCount={activeFilterCount}
              onOpenFilters={() => {
                trackEvent('client-opens-browse-filters-modal');
                setFilterModalOpen(true);
              }}
              pillsContainerRef={pillsContainerRef}
            />
          </div>

          <div ref={pillsContainerRef} className={styles.pillsContainer} />
        </div>

        <div ref={hitsContainerRef} className={styles.resultsContainer} />
        <div
          ref={paginationContainerRef}
          className={styles.paginationContainer}
        />
      </div>

      {/* Filter modal — always rendered, visibility toggled internally */}
      <FilterModal
        search={searchInstance}
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
      />

      <InviteProjectChooser
        modal={inviteChooserModal}
        collaborations={collaborations}
        onSendInvite={sendInviteForBrief}
        onCloseModal={() => setInviteChooserModal(null)}
        isBriefInviteDisabled={isBriefInviteDisabled}
      />
    </div>
  );
};

export default Browse;
